import React, {useEffect, useRef, useState} from 'react'
import {Modal, ModalHeader, ModalBody, ModalFooter, Button} from 'reactstrap'
import GenerateBackgroundLoading from './GenerateBackgroundLoading'
import InputSection from './sections/InputSection'
import ResultsGrid from './sections/ResultsGrid'
import FooterButtons from './sections/FooterButtons'
import UnsavedChangesWarningModal from './UnsavedChangesWarningModal'
import LoginPromptModal from './sections/LoginPromptModal'
import {
    generateBackgroundImages,
    convertResolutionToSizeAndRatio,
    loadGenerateBackgroundConfig,
    checkRemainingImageTokens,
} from '../../../../../../services/GenerateBackgroundServices'
import {getAccessToken} from '../../../../../../services/AuthServices'
import {getLinksUploadBackground, uploadTempArtwork} from '../../../../../../services/ArtworkTemplateServices'
import {ReactComponent as ArrowBackIcon} from '../../../../../../assets/icons/icon-arrow-back-blue.svg'
import {downloadImagesAsZip} from '../../../../../../helpers/downloadZip'

const TOKEN_WARNING_MESSAGE =
    'The number of photos you selected exceeds the available number of turns. Please reduce the number of photos or purchase more turns to continue.'

const CreateBackgroundModal = (props) => {
    const {isOpen, toggle, defaultImageUrl} = props
    const [promptText, setPromptText] = useState('')
    const [attachedImages, setAttachedImages] = useState([])
    const [phase, setPhase] = useState('input') // 'input' | 'loading' | 'done'
    const [progress, setProgress] = useState(0)
    const [generatedList, setGeneratedList] = useState([]) // {id, url, selected, regenerating}
    const [selectAll, setSelectAll] = useState(false)
    const loadingTimerRef = useRef(null)
    const defaultImageLoadedRef = useRef(false)
    const [apiResponse, setApiResponse] = useState(null)
    const [selectedResolution, setSelectedResolution] = useState('1000')
    const [selectedResolutionLabel, setSelectedResolutionLabel] = useState('1000 x 1000')
    const [imageCount, setImageCount] = useState('2')
    const [config, setConfig] = useState(null)
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [fileSizeError, setFileSizeError] = useState(false)
    const [showWarningModal, setShowWarningModal] = useState(false)
    const [pendingAction, setPendingAction] = useState(null) // Store the action to execute after confirmation
    const [tokenInfo, setTokenInfo] = useState(null) // Store token info from API
    const [tokenWarningMessage, setTokenWarningMessage] = useState('')
    const [showLoginPromptModal, setShowLoginPromptModal] = useState(false)
    const [loginPromptItems, setLoginPromptItems] = useState([]) // Items to show in LoginPromptModal

    // Load config and token info when modal opens
    useEffect(() => {
        const loadConfig = async () => {
            const loadedConfig = await loadGenerateBackgroundConfig()
            setConfig(loadedConfig)
            if (loadedConfig && loadedConfig.resolution && loadedConfig.resolution.length > 0) {
                const defaultRes = loadedConfig.resolution[0]
                setSelectedResolution(defaultRes.value)
                setSelectedResolutionLabel(defaultRes.label)
            }
        }

        const loadTokenInfo = async () => {
            try {
                const tokenData = await checkRemainingImageTokens()
                setTokenInfo(tokenData)
            } catch (error) {
                console.error('[CreateBackgroundModal] Failed to load token info:', error)
                // Set default token info on error
                setTokenInfo({remaining: 0, remainingTokens: 0})
            }
        }

        if (isOpen) {
            loadConfig()
            loadTokenInfo()
        } else {
            // Reset tokenInfo when modal closes
            setTokenInfo(null)
            setTokenWarningMessage('')
        }
    }, [isOpen])

    // Load default image when modal opens
    useEffect(() => {
        const loadDefaultImage = async () => {
            if (isOpen && defaultImageUrl && phase === 'input' && !defaultImageLoadedRef.current) {
                try {
                    // Fetch the image
                    const response = await fetch(defaultImageUrl)
                    const blob = await response.blob()

                    // Extract filename from URL or use default
                    const urlParts = defaultImageUrl.split('/')
                    const filename = urlParts[urlParts.length - 1].split('?')[0] || 'mockup-image.jpg'

                    // Create a File object from the blob
                    const file = new File([blob], filename, {type: blob.type || 'image/jpeg'})

                    // Create the image item
                    const imageItem = {
                        id: `default-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                        file,
                        previewUrl: URL.createObjectURL(blob),
                        fullWidth: false,
                    }

                    setAttachedImages([imageItem])
                    defaultImageLoadedRef.current = true
                } catch (error) {
                    console.error('Error loading default image:', error)
                }
            }
        }

        if (isOpen && phase === 'input') {
            loadDefaultImage()
        }

        // Cleanup: reset when modal closes
        if (!isOpen) {
            setAttachedImages([])
            setPromptText('')
            setPhase('input')
            setGeneratedList([])
            defaultImageLoadedRef.current = false
            setFileSizeError(false)
            setLoginPromptItems([])
        }
    }, [isOpen, defaultImageUrl, phase])

    // Check if user is logged in
    useEffect(() => {
        const accessToken = getAccessToken()
        setIsLoggedIn(!!accessToken)
    }, [isOpen])

    // Check tokens and display warning when imageCount changes
    useEffect(() => {
        const checkTokensForWarning = async () => {
            if (!tokenInfo || !imageCount) {
                setTokenWarningMessage('')
                return
            }

            const requestedCount = parseInt(imageCount) || 1
            const remainingTokens = tokenInfo?.remaining || tokenInfo?.remainingTokens || 0

            if (remainingTokens <= 0) {
                setTokenWarningMessage('')
                return
            }

            if (remainingTokens < requestedCount) {
                setTokenWarningMessage(TOKEN_WARNING_MESSAGE)
            } else {
                setTokenWarningMessage('')
            }
        }

        checkTokensForWarning()
    }, [imageCount, tokenInfo])

    // Validate image file size and upload errors
    const validateImageFile = () => {
        const maxFileSize = config?.maxFileSize || 7 * 1024 * 1024 // Default 7MB in bytes

        if (attachedImages.length === 0) {
            return true
        }

        const imageItem = attachedImages[0]

        // Check if upload failed
        if (imageItem?.uploadError) {
            setFileSizeError(true)
            return false
        }

        // Check file size if image has file (not yet uploaded)
        if (imageItem?.file) {
            const fileSize = imageItem.file.size
            if (fileSize > maxFileSize) {
                setFileSizeError(true)
                return false
            }
        }

        setFileSizeError(false)
        return true
    }

    // Validate prompt text length
    const validatePromptText = () => {
        const minPromptLength = config?.minPromptLength || 20
        if (!promptText || promptText.trim().length < minPromptLength) {
            return false
        }
        return true
    }

    // Upload blob image to presigned URL
    const uploadBlobImageToPresignedUrl = async (imageItem) => {
        try {
            // Get presigned URL for upload
            const {success, data: presignedUrlData} = await getLinksUploadBackground({limit: 1})

            if (!success || !presignedUrlData) {
                throw new Error('Failed to get presigned URL for image upload')
            }

            // Extract URL from response (response can be array or object with {url, key})
            const presignedUrlObj = Array.isArray(presignedUrlData) ? presignedUrlData[0] : presignedUrlData
            const presignedUrl = presignedUrlObj?.url || presignedUrlObj

            if (!presignedUrl || typeof presignedUrl !== 'string') {
                throw new Error('Invalid presigned URL format')
            }

            // Upload image to presigned URL
            await uploadTempArtwork(presignedUrl, imageItem.file, imageItem.file.type || 'image/jpeg')

            // Extract the image URL from presigned URL (remove query parameters)
            const imageUrl = presignedUrl.split('?')[0]

            return imageUrl
        } catch (error) {
            console.error('[CreateBackgroundModal] Failed to upload default image:', error)
            throw error
        }
    }

    // Prepare image URL (upload blob if needed)
    const prepareImageUrl = async () => {
        if (attachedImages.length === 0) {
            return {finalImageUrl: null, updatedAttachedImages: []}
        }

        const imageItem = attachedImages[0]

        // If image has imageUrl, use it directly
        if (imageItem?.imageUrl) {
            return {
                finalImageUrl: imageItem.imageUrl,
                updatedAttachedImages: attachedImages,
            }
        }

        // If image has file but no imageUrl (default image from blob), upload it first
        if (imageItem?.file) {
            const finalImageUrl = await uploadBlobImageToPresignedUrl(imageItem)

            // Update attachedImages with imageUrl
            const updatedImageItem = {
                ...imageItem,
                imageUrl: finalImageUrl,
            }
            const updatedAttachedImages = [updatedImageItem]
            setAttachedImages(updatedAttachedImages)

            return {finalImageUrl, updatedAttachedImages}
        }

        return {finalImageUrl: null, updatedAttachedImages: attachedImages}
    }

    // Check and validate tokens
    const checkAndValidateTokens = async (requestedCount) => {
        const ensureTokensAvailable = (remainingTokens) => {
            if (remainingTokens <= 0) {
                setPhase('input')
                setTokenWarningMessage('')
                setLoginPromptItems(generatedList.length > 0 ? generatedList : [])
                setShowLoginPromptModal(true)
                return false
            }
            if (remainingTokens < requestedCount) {
                setPhase('input')
                setTokenWarningMessage(TOKEN_WARNING_MESSAGE)
                return false
            }
            setTokenWarningMessage('')
            return true
        }

        // First check tokenInfo state if available
        if (tokenInfo) {
            const remainingTokens = tokenInfo?.remaining || tokenInfo?.remainingTokens || 0
            if (!ensureTokensAvailable(remainingTokens)) {
                return false
            }
        }

        // If tokenInfo not available or need to refresh, call API
        try {
            const updatedTokenInfo = await checkRemainingImageTokens()
            setTokenInfo(updatedTokenInfo)

            const remainingTokens = updatedTokenInfo?.remaining || updatedTokenInfo?.remainingTokens || 0

            if (!ensureTokensAvailable(remainingTokens)) {
                return false
            }
        } catch (error) {
            console.error('[GenerateBackground] Failed to check remaining tokens:', error)
            // Continue with generation even if token check fails (let API handle it)
        }

        return true
    }

    // Generate images via API
    const generateImages = async (finalImageUrl, size, ratio) => {
        try {
            const {data, status} = await generateBackgroundImages({
                imageUrl: finalImageUrl,
                promptText,
                count: imageCount,
                size,
                ratio,
            })

            console.log('[GenerateBackground][response]', {status, data})
            setApiResponse({status, data})

            // Normalize payload shape and map results to UI list
            const payload = data && data.data ? data.data : data
            const images = payload && Array.isArray(payload.images) ? payload.images : []
            const mapped = images.map((img, idx) => ({
                id: `bg-${img.index ?? idx}`,
                url: img.url,
                key: img.key,
                selected: false,
            }))
            setGeneratedList(mapped)
            setSelectAll(false)

            // Update token info after successful generation
            try {
                const updatedTokenInfo = await checkRemainingImageTokens()
                setTokenInfo(updatedTokenInfo)
            } catch (error) {
                console.error('[GenerateBackground] Failed to update token info after generation:', error)
            }

            setProgress(100)
            setPhase('done')
        } catch (error) {
            console.error('[GenerateBackground][error]', error)
            setApiResponse({error: true, detail: error?.response || error?.message || error})
            setProgress(100)
            setGeneratedList([])
            setSelectAll(false)
            setPhase('done')
        }
    }

    const handleSubmit = async () => {
        // Validate image file
        if (!validateImageFile()) {
            return
        }

        // Validate prompt text
        if (!validatePromptText()) {
            return
        }

        // Check and validate tokens
        const requestedCount = parseInt(imageCount) || 1
        setTokenWarningMessage('')

        const tokensValid = await checkAndValidateTokens(requestedCount)
        if (!tokensValid) {
            return
        }

        // Switch to loading state BEFORE getting presigned link
        setPhase('loading')
        setProgress(0)

        // Prepare image URL (upload blob if needed)
        let finalImageUrl = null
        let updatedAttachedImages = attachedImages
        try {
            const result = await prepareImageUrl()
            finalImageUrl = result.finalImageUrl
            updatedAttachedImages = result.updatedAttachedImages
        } catch (error) {
            setFileSizeError(true)
            setPhase('input')
            return
        }

        // Notify parent component
        if (props.onSubmit) {
            props.onSubmit({promptText, attachedImages: updatedAttachedImages})
        }

        // Convert resolution to size and ratio
        const {size, ratio} = convertResolutionToSizeAndRatio(selectedResolution, selectedResolutionLabel)

        // Generate images
        await generateImages(finalImageUrl, size, ratio)
    }

    useEffect(() => {
        if (phase !== 'loading') return

        // Calculate expected duration based on number of images, resolution, size, and ratio
        const calculateExpectedDuration = (count, resolutionValue, resolutionLabel) => {
            // Base duration per image count (for 1k resolution)
            const baseDurations = {
                1: 10000, // 10 seconds for 1 image at 1k
                2: 13000, // 13 seconds for 2 images at 1k
                3: 17000, // 17 seconds for 3 images at 1k
                4: 20000, // 20 seconds for 4 images at 1k
            }

            const baseDuration = baseDurations[count] || 13000

            // Convert resolution to size and ratio to get multipliers
            const {size, ratio} = convertResolutionToSizeAndRatio(resolutionValue, resolutionLabel)

            // Size multipliers (higher resolution = longer time)
            const sizeMultipliers = {
                '1k': 1.0, // Base multiplier
                '2k': 2, // 1.5x longer for 2k
                '4k': 3, // 2.2x longer for 4k
                '8k': 4, // 3.5x longer for 8k
            }

            const sizeMultiplier = sizeMultipliers[size] || 1.0

            // Ratio multipliers (wider/taller ratios may take slightly longer)
            // Parse ratio to get width and height
            const ratioParts = ratio.split(':')
            const ratioWidth = parseInt(ratioParts[0]) || 1
            const ratioHeight = parseInt(ratioParts[1]) || 1
            const aspectRatio = ratioWidth / ratioHeight

            // More extreme aspect ratios (very wide or very tall) may take slightly longer
            let ratioMultiplier = 1.0
            if (aspectRatio > 2 || aspectRatio < 0.5) {
                ratioMultiplier = 1.2 // 20% longer for extreme ratios
            } else if (aspectRatio > 1.5 || aspectRatio < 0.67) {
                ratioMultiplier = 1.1 // 10% longer for moderate ratios
            }

            // Calculate final duration
            const expectedDuration = baseDuration * sizeMultiplier * ratioMultiplier

            return Math.round(expectedDuration)
        }

        const start = Date.now()
        const expectedDurationMs = calculateExpectedDuration(imageCount, selectedResolution, selectedResolutionLabel)
        const maxHold = 97 // Stop at 97% until API completes
        const intervalMs = 100
        const id = setInterval(() => {
            const elapsed = Date.now() - start
            const ratio = Math.min(elapsed / expectedDurationMs, maxHold / 100)
            const next = Math.floor(ratio * 100)
            setProgress((prev) => (next > prev ? next : prev))
        }, intervalMs)
        loadingTimerRef.current = id
        return () => clearInterval(id)
    }, [phase, imageCount, selectedResolution, selectedResolutionLabel])

    const toggleSelectAll = () => {
        const next = !selectAll
        setSelectAll(next)
        setGeneratedList((list) => list.map((it) => ({...it, selected: next})))
    }

    const toggleSelect = (id) => {
        setGeneratedList((list) => {
            const next = list.map((it) => (it.id === id ? {...it, selected: !it.selected} : it))
            setSelectAll(next.every((it) => it.selected))
            return next
        })
    }

    const handleDownload = async (url) => {
        try {
            // Fetch the image as a blob to handle cross-origin issues
            const response = await fetch(url)
            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)

            // Extract filename from URL or use a default
            const filename = url.split('/').pop().split('?')[0] || 'background-image.jpg'

            const a = document.createElement('a')
            a.href = blobUrl
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            // Clean up the blob URL
            window.URL.revokeObjectURL(blobUrl)
        } catch (e) {
            console.error('Failed to download image:', e)
            // Fallback: try to open in new tab if download fails
            window.open(url, '_blank')
        }
    }

    const handleAddBackground = () => {
        const selectedItems = generatedList.filter((it) => it.selected)
        if (props.onAddBackground) props.onAddBackground(selectedItems)
        toggle()
    }

    const _handleChangeSelect = (e) => {
        const value = e.target.value
        setSelectedResolution(value)
        // Find the label from config
        if (config && config.resolution) {
            const selectedRes = config.resolution.find((res) => res.value === value)
            if (selectedRes) {
                setSelectedResolutionLabel(selectedRes.label)
            }
        }
    }

    const handleRegenerate = async (itemId, imageUrl) => {
        // Check remaining tokens before regenerating (regenerating 1 image costs 1 token)
        const requestedCount = 1

        // First check tokenInfo state if available
        if (tokenInfo) {
            const remainingTokens = tokenInfo?.remaining || tokenInfo?.remainingTokens || 0
            if (remainingTokens <= 0) {
                // Pass current generatedList items to LoginPromptModal
                setLoginPromptItems(generatedList)
                setShowLoginPromptModal(true)
                return
            }
        }

        // If tokenInfo not available or need to refresh, call API
        try {
            const updatedTokenInfo = await checkRemainingImageTokens()
            setTokenInfo(updatedTokenInfo)

            const remainingTokens = updatedTokenInfo?.remaining || updatedTokenInfo?.remainingTokens || 0

            // Check if user has enough tokens
            if (remainingTokens <= 0) {
                // Pass current generatedList items to LoginPromptModal
                setLoginPromptItems(generatedList)
                setShowLoginPromptModal(true)
                return
            }
        } catch (error) {
            console.error('[RegenerateBackground] Failed to check remaining tokens:', error)
            // Continue with regeneration even if token check fails (let API handle it)
        }

        // Set regenerating state for this item
        setGeneratedList((list) => list.map((it) => (it.id === itemId ? {...it, regenerating: true} : it)))

        // Convert resolution to size and ratio
        const {size, ratio} = convertResolutionToSizeAndRatio(selectedResolution, selectedResolutionLabel)

        try {
            const {data, status} = await generateBackgroundImages({
                imageUrl,
                promptText,
                count: '1',
                size,
                ratio,
            })

            // Normalize payload shape
            const payload = data && data.data ? data.data : data
            const images = payload && Array.isArray(payload.images) ? payload.images : []

            if (images.length > 0) {
                const newImage = images[0]
                // Update the specific item with new image URL and key, keep regenerating=true
                setGeneratedList((list) =>
                    list.map((it) =>
                        it.id === itemId ? {...it, url: newImage.url, key: newImage.key} : it
                    )
                )
            } else {
                // No response, don't update image but clear regenerating
                setGeneratedList((list) => list.map((it) => (it.id === itemId ? {...it, regenerating: false} : it)))
            }

            // Update token info after successful regeneration
            try {
                const updatedTokenInfo = await checkRemainingImageTokens()
                setTokenInfo(updatedTokenInfo)
            } catch (error) {
                console.error('[GenerateBackground] Failed to update token info after regeneration:', error)
            }
        } catch (error) {
            console.error('[GenerateBackground][regenerate][error]', error)
            // On error, don't update and remove loading state
            setGeneratedList((list) => list.map((it) => (it.id === itemId ? {...it, regenerating: false} : it)))
        }
    }

    const handleImageLoaded = (itemId) => {
        setGeneratedList((list) =>
            list.map((it) => (it.id === itemId ? {...it, regenerating: false} : it))
        )
    }

    const handleBackToInput = () => {
        if (phase === 'done') {
            setPendingAction(() => () => setPhase('input'))
            setShowWarningModal(true)
        } else {
            setPhase('input')
        }
    }

    const handleToggle = () => {
        if (phase === 'done') {
            setPendingAction(() => () => toggle())
            setShowWarningModal(true)
        } else {
            toggle()
        }
    }

    const handleCancel = () => {
        if (phase === 'done') {
            setPendingAction(() => () => toggle())
            setShowWarningModal(true)
        } else {
            toggle()
        }
    }

    const handleConfirmWarning = () => {
        setShowWarningModal(false)
        if (pendingAction) {
            pendingAction()
            setPendingAction(null)
        }
    }

    const handleCancelWarning = () => {
        setShowWarningModal(false)
        setPendingAction(null)
    }

    return (
        <>
            <Modal isOpen={isOpen} toggle={handleToggle} className="CreateBackgroundModal" centered>
                <ModalHeader toggle={handleToggle}>
                    {phase === 'done' && (
                        <button
                            type="button"
                            className="btn btn-link p-0 me-3"
                            onClick={handleBackToInput}
                            style={{border: 'none', background: 'none', cursor: 'pointer', marginTop: '-4px'}}
                            aria-label="Back to input"
                        >
                            <ArrowBackIcon style={{width: '20px', height: '20px'}} />
                        </button>
                    )}
                    AI Background generates
                </ModalHeader>
                <ModalBody className="CreateBackgroundBody">
                    {phase === 'input' && (
                        <InputSection
                            promptText={promptText}
                            setPromptText={setPromptText}
                            attachedImages={attachedImages}
                            setAttachedImages={setAttachedImages}
                            onChangeResolution={_handleChangeSelect}
                            imageCount={imageCount}
                            setImageCount={setImageCount}
                            selectedResolution={selectedResolution}
                            fileSizeError={fileSizeError}
                            setFileSizeError={setFileSizeError}
                            tokenInfo={tokenInfo}
                            tokenWarningMessage={tokenWarningMessage}
                        />
                    )}
                    {phase === 'loading' && (
                        <div className="py-10">
                            <GenerateBackgroundLoading percentage={progress} />
                        </div>
                    )}
                    {phase === 'done' && (
                        <ResultsGrid
                            selectAll={selectAll}
                            onToggleSelectAll={toggleSelectAll}
                            items={generatedList}
                            onToggleSelect={toggleSelect}
                            onDownload={handleDownload}
                            onRegenerate={handleRegenerate}
                            onImageLoaded={handleImageLoaded}
                            config={config}
                            tokenInfo={tokenInfo}
                            isLoggedIn={isLoggedIn}
                        />
                    )}
                </ModalBody>
                <ModalFooter>
                    <FooterButtons
                        phase={phase}
                        onCancel={handleCancel}
                        onSubmit={handleSubmit}
                        onAdd={handleAddBackground}
                        canSubmit={
                            phase === 'input' &&
                            promptText &&
                            promptText.trim().length >= (config?.minPromptLength || 20) &&
                            selectedResolution &&
                            imageCount &&
                            !tokenWarningMessage
                        }
                        canAdd={generatedList.filter((it) => it.selected).length > 0}
                    />
                </ModalFooter>
            </Modal>
            <UnsavedChangesWarningModal
                isOpen={showWarningModal}
                onConfirm={handleConfirmWarning}
                onCancel={handleCancelWarning}
            />
            <LoginPromptModal
                isOpen={showLoginPromptModal}
                toggle={() => {
                    setShowLoginPromptModal(false)
                    setLoginPromptItems([])
                }}
                onLogin={() => {
                    setShowLoginPromptModal(false)
                    setLoginPromptItems([])
                    window.location.href = `${process.env.REACT_APP_BASE_URL}/login`
                }}
                onDownloadAndLogin={async () => {
                    // Download all items as zip before login
                    if (loginPromptItems && loginPromptItems.length > 0) {
                        try {
                            await downloadImagesAsZip(loginPromptItems, 'ai-backgrounds.zip', 'background_')
                            // Navigate to login after a short delay to ensure download starts
                            setTimeout(() => {
                                setShowLoginPromptModal(false)
                                setLoginPromptItems([])
                                window.location.href = `${process.env.REACT_APP_BASE_URL}/login`
                            }, 500)
                        } catch (error) {
                            console.error('Failed to download zip file:', error)
                            // If zip creation fails, just navigate to login
                            setShowLoginPromptModal(false)
                            setLoginPromptItems([])
                            window.location.href = `${process.env.REACT_APP_BASE_URL}/login`
                        }
                    } else {
                        setShowLoginPromptModal(false)
                        setLoginPromptItems([])
                        window.location.href = `${process.env.REACT_APP_BASE_URL}/login`
                    }
                }}
                onPurchase={() => {
                    console.log('[CreateBackgroundModal] Purchase clicked')
                    // TODO: Implement purchase flow
                }}
                items={loginPromptItems}
                isLoggedIn={isLoggedIn}
            />
        </>
    )
}

export default CreateBackgroundModal

